import type {
  Comentario,
  DatosDetalle,
  DatosIndirectos,
  DatosIngresos,
  DatosMovimientosUtilidad,
  DatosNeta,
  DatosObras,
  DatosResumen,
  Ejemplo,
  GuardarComentarioInput,
  OpcionFiltro,
  RespuestaApi,
} from '@adelante/shared';
import { modoDev, msalInstance, obtenerIniciales, scopesApi, usuarioDev } from './auth';

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Obtiene un access token para llamar a la API Adelante.
 * - En modo dev: devuelve `null` (el backend skipea validación con AUTH_DEV_MODE=true).
 * - En modo real: pide token silencioso a MSAL para el scope custom de la API.
 */
async function obtenerToken(): Promise<string | null> {
  if (modoDev) return null;

  if (!msalInstance) {
    throw new Error('MSAL no está inicializado');
  }

  const cuentas = msalInstance.getAllAccounts();
  const cuenta = cuentas[0];
  if (!cuenta) {
    throw new Error('Sin sesión activa');
  }

  const respuesta = await msalInstance.acquireTokenSilent({
    scopes: scopesApi,
    account: cuenta,
  });

  return respuesta.accessToken;
}

/**
 * Wrapper genérico sobre `fetch` que añade auth, content-type y maneja errores
 * HTTP a alto nivel. Las funciones del cliente lo usan internamente.
 */
async function pedir<T>(ruta: string, opciones?: RequestInit): Promise<T> {
  const token = await obtenerToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((opciones?.headers ?? {}) as Record<string, string>),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const respuesta = await fetch(`${API_URL}${ruta}`, { ...opciones, headers });

  if (!respuesta.ok) {
    throw new Error(`HTTP ${respuesta.status}: ${respuesta.statusText}`);
  }

  return (await respuesta.json()) as T;
}

/**
 * Cliente API tipado. Cada agrupación corresponde a un módulo del backend.
 * Para agregar un endpoint nuevo: extender este objeto y reutilizar `pedir<T>`.
 */
/**
 * Params de período aceptados por los endpoints de utilidades.
 * Admite el formato nuevo (rango) que es el que usa el frontend moderno,
 * y opcionalmente el legacy (anio + mes) por backwards-compat.
 */
type ParamsRangoUtilidad = {
  desdeAnio?: number;
  desdeMes?: number;
  hastaAnio?: number;
  hastaMes?: number;
  anio?: number;
  mes?: number;
};

function qs(params: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const cliente = {
  ejemplo: {
    listar: () => pedir<RespuestaApi<Ejemplo[]>>('/api/ejemplo'),
  },
  catalogos: {
    tiposMovimiento: () => pedir<RespuestaApi<OpcionFiltro[]>>('/api/catalogos/tipos-movimiento'),
    lotes: () => pedir<RespuestaApi<OpcionFiltro[]>>('/api/catalogos/lotes'),
  },
  utilidades: {
    // Los endpoints aceptan tanto el formato legacy (anio + mes) como el
    // nuevo de rango (desdeAnio/desdeMes/hastaAnio/hastaMes). El frontend
    // moderno siempre manda rango via usePeriodo().parametrosAPI().
    resumen: (p: ParamsRangoUtilidad & { tipos?: string; lotes?: string }) =>
      pedir<RespuestaApi<DatosResumen>>(`/api/utilidades/resumen${qs(p)}`),
    ingresos: (p: ParamsRangoUtilidad & { lotes?: string }) =>
      pedir<RespuestaApi<DatosIngresos>>(`/api/utilidades/ingresos${qs(p)}`),
    detalle: (
      p: ParamsRangoUtilidad & {
        tipos?: string;
        lotes?: string;
        busqueda?: string;
        pagina?: number;
        porPagina?: number;
      },
    ) => pedir<RespuestaApi<DatosDetalle>>(`/api/utilidades/detalle${qs(p)}`),
    neta: (p?: ParamsRangoUtilidad) =>
      pedir<RespuestaApi<DatosNeta>>(`/api/utilidades/neta${qs(p ?? {})}`),
    movimientos: (p: ParamsRangoUtilidad & { tipos?: string; lotes?: string }) =>
      pedir<RespuestaApi<DatosMovimientosUtilidad>>(`/api/utilidades/movimientos${qs(p)}`),
    indirectos: (p: ParamsRangoUtilidad & { lotes?: string }) =>
      pedir<RespuestaApi<DatosIndirectos>>(`/api/utilidades/indirectos${qs(p)}`),
    obras: () => pedir<RespuestaApi<DatosObras>>('/api/utilidades/obras'),
  },
  comentarios: {
    listar: (p: { anio: number; mes: number }) =>
      pedir<RespuestaApi<{ comentarios: Comentario[] }>>(`/api/comentarios${qs(p)}`),
    guardar: (body: GuardarComentarioInput) =>
      pedir<RespuestaApi<{ comentario: Comentario }>>('/api/comentarios', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    eliminar: (id: number) =>
      pedir<RespuestaApi<{ eliminado: boolean }>>(`/api/comentarios/${id}`, {
        method: 'DELETE',
      }),
  },
};

/** Información del usuario activo (modo dev o MSAL real). */
export interface UsuarioActivo {
  name: string;
  username: string;
  iniciales: string;
}

/**
 * Obtiene el usuario activo. En modo dev devuelve `usuarioDev`.
 * En modo MSAL devuelve la primera cuenta o `null` si no hay sesión.
 */
export function obtenerUsuarioActual(): UsuarioActivo | null {
  if (modoDev) return { ...usuarioDev };

  const cuenta = msalInstance?.getAllAccounts()[0];
  if (!cuenta) return null;

  const nombre = cuenta.name ?? cuenta.username;
  return {
    name: nombre,
    username: cuenta.username,
    iniciales: obtenerIniciales(nombre),
  };
}
